"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { FiX, FiTag, FiFileText } from "react-icons/fi"
import toast from "react-hot-toast"
import api from "../../libs/axios"

enum CategoryStatus {
    ACTIVE = "active",
    INACTIVE = "inactive",
}

interface EditMatchCategoryOffcanvasProps {
    categoryId: string
    showEditCategoryCanvas: boolean
    setShowEditCategoryCanvas: (show: boolean) => void
    onCategoryUpdated?: () => void
}

interface CategoryFormData {
    name: string
    description: string
    status: CategoryStatus
}

const EditMatchCategoryOffcanvas: React.FC<EditMatchCategoryOffcanvasProps> = ({
    categoryId,
    showEditCategoryCanvas,
    setShowEditCategoryCanvas,
    onCategoryUpdated,
}) => {
    const [formData, setFormData] = useState<CategoryFormData>({
        name: "",
        description: "",
        status: CategoryStatus.ACTIVE,
    })

    const [errors, setErrors] = useState<Partial<CategoryFormData>>({})
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    // Fetch category data when component opens
    useEffect(() => {
        if (showEditCategoryCanvas && categoryId) {
            fetchCategoryData()
        }
    }, [showEditCategoryCanvas, categoryId])

    const fetchCategoryData = async () => {
        setLoading(true)
        try {
            const response = await api.get(`/match-categories/${categoryId}`)
            const category = response.data.category

            setFormData({
                name: category.name || "",
                description: category.description || "",
                status: category.status || CategoryStatus.ACTIVE,
            })
        } catch (error: any) {
            console.error('Error fetching category data:', error)
            toast.error('Failed to fetch category data')
        } finally {
            setLoading(false)
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))

        // Clear error when user starts typing
        if (errors[name as keyof CategoryFormData]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }))
        }
    }

    const validateForm = (): boolean => {
        const newErrors: Partial<CategoryFormData> = {}

        // Name validation (required, minimum length)
        if (!formData.name.trim()) {
            newErrors.name = "Category name is required"
        } else if (formData.name.trim().length < 2) {
            newErrors.name = "Category name must be at least 2 characters"
        } else if (formData.name.trim().length > 50) {
            newErrors.name = "Category name must not exceed 50 characters"
        }

        // Description validation (required, minimum length)
        if (!formData.description.trim()) {
            newErrors.description = "Description is required"
        } else if (formData.description.trim().length < 10) {
            newErrors.description = "Description must be at least 10 characters"
        } else if (formData.description.trim().length > 200) {
            newErrors.description = "Description must not exceed 200 characters"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error("Please fix the validation errors before submitting")
            return
        }

        setSubmitting(true)
        try {
            const categoryData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                status: formData.status
            }

            await api.put(`/match-categories/${categoryId}`, categoryData)
            toast.success("Match category updated successfully")

            // Call the callback to refresh the list
            if (onCategoryUpdated) {
                onCategoryUpdated()
            }

            handleClose()
        } catch (error: any) {
            console.error('Error updating match category:', error)
            let errorMessage = 'Failed to update match category'

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message
            }

            toast.error(errorMessage)
        } finally {
            setSubmitting(false)
        }
    }

    const resetForm = () => {
        setFormData({
            name: "",
            description: "",
            status: CategoryStatus.ACTIVE,
        })
        setErrors({})
    }

    const handleClose = () => {
        if (!submitting) {
            resetForm()
            setShowEditCategoryCanvas(false)
        }
    }

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${showEditCategoryCanvas ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${showEditCategoryCanvas ? "translate-x-0" : "translate-x-full"
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiTag className="mr-2" />
                        Edit Match Category
                    </h2>
                    <button
                        onClick={handleClose}
                        disabled={submitting}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <div className="flex flex-col h-full">
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
                        </div>
                    ) : (
                        <>
                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FiTag className="inline mr-1" />
                                            Category Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            disabled={submitting}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.name ? "border-red-500" : "border-gray-300"
                                                }`}
                                            placeholder="Enter category name (e.g., T20, ODI, Test Match)"
                                        />
                                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FiFileText className="inline mr-1" />
                                            Description *
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            disabled={submitting}
                                            rows={4}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${errors.description ? "border-red-500" : "border-gray-300"
                                                }`}
                                            placeholder="Enter category description (e.g., Twenty20 cricket matches with 20 overs per side)"
                                        />
                                        {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                                        <p className="text-xs text-gray-500 mt-1">{formData.description.length}/200 characters</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            disabled={submitting}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                                        >
                                            <option value={CategoryStatus.ACTIVE}>Active</option>
                                            <option value={CategoryStatus.INACTIVE}>Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="border-t border-gray-200 p-6 mb-20">
                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Updating...
                                            </div>
                                        ) : (
                                            'Update Category'
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={submitting}
                                        className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    )
}

export default EditMatchCategoryOffcanvas